import { useState } from 'react';
import {
  AgencyTypeIcon,
  Badge,
  BUTTON_CLASS,
  dangerButtonStyle,
  DetailDrawer,
  formatUtcDate,
  neutralButtonStyle,
  primaryButtonStyle,
  StatusDot,
  token,
  WRAPPING_BUTTON_CLASS,
  type BadgeTone,
} from '@bp-files/ui';
import { SPACE_LIST_COPY } from '@/components/SpaceList/spaceList.copy';
import { RowActionsItem, RowActionsMenu } from '@/components/SpaceList/RowActionsMenu';
import {
  acceptMembership,
  deleteSpace,
  removeMembership,
  setMemberScope,
  type SpaceMemberRef,
  type SpaceSummary,
  type VisibilityScope,
} from '@/lib/api';
import { ApiError, messageFrom } from '@bp-files/client';

/**
 * A space's roster, and every act that changes it — CON-11 as amended, CON-14,
 * CON-22, CON-23, EXIT-7.
 *
 * **This is the surface a room needed and a link never could have had.** A link
 * had a counterpart, so "who is in this" was a field; a space has a roster, so
 * it is a list — and every one of CON-14's verbs, CON-6's acceptance and CON-7's
 * invitation is an act on one row of it. Each membership carries its own status
 * (CON-23): a room can hold three `Active` members, two `Invited` and one
 * `Removed`, and no code here computes a single verdict from that list because
 * none of the six would be described by it.
 *
 * **The reader's own agency is on the list, not filtered out of it.** It is what
 * makes Leave a row action beside Remove rather than a button somewhere else,
 * and it is what the API sends (CON-11 was crossed deliberately to publish the
 * whole roster — ADR-0066).
 *
 * **Every control here is visibility only.** The owner clause on Add and Remove
 * comes from `isOwner` on the wire; SEC-5 puts the decision in the API, which
 * refuses on every request from the token-resolved identity whatever this
 * rendered. Hiding a control is a courtesy, never a boundary.
 *
 * **After a mutation the caller refetches — this component patches nothing.**
 * A status, an owner's own row, and the space's last activity all move together,
 * so the server's answer is the only one worth rendering (§4's standing rule).
 *
 * **Scope is on every row and its control is on almost none** (CON-24 as
 * amended). The value is shown to every member about every member, because an
 * agency deciding whether to put evidence in a room needs to know who will be
 * reading it — the same reason the roster is published at all, and a stronger
 * one. Changing it is the **owner's**, one of §5.3's rows that belong to the
 * agency that opened the room rather than to any admin in it. Two values
 * make the change a toggle, so it is a single menu item naming the scope the
 * member is *not* at rather than a picker (ADR-0067: there is no third answer).
 *
 * **The owner cannot narrow itself**, and that is the one gate here with a
 * consequence rather than a rule behind it: `own` has no exception for the owner
 * (CON-24), so an owner that could set its own scope could make the room it
 * convened unreadable to itself in one click. The API would allow it — nothing
 * in `spaceMemberScope` forbids naming your own agency — so this is the rare
 * case where the UI is stricter than the wire, and it is stated here rather than
 * inferred from the absence of a menu item.
 *
 * ~~**What is deliberately not here:** a rename, which the API offers and no
 * screen asks for yet.~~
 *
 * _Amended (**ADR-0102**)._ **The rename exists, and it is not here.** It is an
 * item in the row's overflow opening a drawer of its own, because every control
 * in this panel names an agency and a rename names none — this panel is the
 * roster. Delete below is the deliberate exception, and only because a room is
 * deleted *after* it has been emptied and the roster is where an owner finds
 * that out. The sentence above stood for six milestones over a
 * `PATCH /spaces/{id}` that had shipped, was owner-gated, and had no caller.
 *
 * _Amended (CON-26)._ **Deleting a space is here now**, and the sentence that
 * said there was no way to is struck: ~~"any way to delete a space, because
 * there is none — an owner leaving its own room orphans it"~~. The orphan that
 * paragraph worried about is what CON-26 answers, and it answers it narrowly —
 * only the owner, and only once every file in the room has been purged, so the
 * act can never be the thing that destroys evidence. It sits below the roster
 * rather than beside Add an agency, because a control that ends the room should
 * not share an edge with the list of people it would affect.
 */

export interface MembersPanelProps {
  open: boolean;
  onClose: () => void;
  /** The room. Its roster is the list, and its id addresses every act. */
  space: SpaceSummary;
  /**
   * The reader's own agency — which row is *us*.
   *
   * **Not the shim `AgencyGrid` shed.** That prop existed to find "the one other
   * agency"; this one separates leaving from removing, which is a distinction
   * CON-14 makes at any roster size and one route (`DELETE
   * /spaces/{id}/members/{aid}`) expresses only through its target.
   */
  viewerAgencyId: string;
  /**
   * A one-line report about the act that opened this panel, or nothing.
   *
   * Its only source today is `AddAgencyPanel`'s branch — see `onAdded` there.
   * **`SpaceList` owns the string and its lifetime**, because the panel that
   * produces it is not this one and because it must not outlive the roster it
   * describes: closing, adding again, or any membership change clears it.
   *
   * Required and nullable rather than optional, on `FolderView`'s convention for
   * the same shape — `exactOptionalPropertyTypes` is on, so an optional prop
   * cannot be handed a `string | undefined` without the caller branching.
   */
  notice: string | undefined;
  /** Owner-only, and it opens `AddAgencyPanel` in this drawer's place. */
  onAddAgency: () => void;
  /** Called after any mutation, so the list refetches. */
  onChanged: () => void;
  /**
   * Called once the room itself is gone (CON-26).
   *
   * Separate from `onChanged` because the drawer has to **close**: there is no
   * roster left to hand back to, and a refetch alone would leave a modal open
   * over a space that no longer exists.
   */
  onDeleted: () => void;
}

/**
 * Status tones. `Invited` is amber because the design system reserves amber for
 * time and for *active* — an invitation in flight is exactly that. `Removed` is
 * not alert-red: removal is a normal administrative act (CON-14), not a fault,
 * and colouring it as one would misread the room.
 */
const STATUS_TONE: Record<SpaceMemberRef['status'], BadgeTone> = {
  Invited: 'amber',
  Active: 'update',
  Removed: 'neutral',
};

/**
 * Which two-step confirmation is open, and on which row.
 *
 * `scope` carries the value being moved **to** rather than a bare flag, because
 * a row's own scope is what decides which direction is on offer — and the
 * confirmation has to name the outcome after the menu that named the act has
 * already closed.
 */
type Asked = { kind: 'remove' | 'leave' } | { kind: 'scope'; scope: VisibilityScope };

type Confirming = Asked & { agencyId: string };

/** The value a member is not at. Two values, so the change is a toggle. */
function otherScope(scope: VisibilityScope): VisibilityScope {
  return scope === 'all' ? 'own' : 'all';
}

export function MembersPanel({
  open,
  onClose,
  space,
  viewerAgencyId,
  notice,
  onAddAgency,
  onChanged,
  onDeleted,
}: MembersPanelProps) {
  const [busyAgencyId, setBusyAgencyId] = useState<string | undefined>();
  const [confirming, setConfirming] = useState<Confirming | undefined>();
  const [actionFailed, setActionFailed] = useState(false);
  // CON-26's two stages, kept apart from `confirming` above: that one is keyed
  // by agency because it is a per-row act, and this one is about the room.
  const [deleteAsked, setDeleteAsked] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | undefined>(undefined);

  const viewerIsOwner = space.members.some(
    (member) => member.agencyId === viewerAgencyId && member.isOwner,
  );

  async function run(agencyId: string, action: () => Promise<unknown>) {
    setBusyAgencyId(agencyId);
    setActionFailed(false);

    try {
      await action();
      setConfirming(undefined);
      onChanged();
    } catch {
      // Deliberately one message for every failure. A 403 here means the world
      // moved under the admin — ownership is not theirs, or the membership has
      // already ended — and the honest instruction in every case is to look
      // again.
      setActionFailed(true);
    } finally {
      setBusyAgencyId(undefined);
    }
  }

  return (
    <DetailDrawer
      open={open}
      onClose={onClose}
      title={SPACE_LIST_COPY.members.title(space.name)}
      closeLabel={SPACE_LIST_COPY.members.close}
    >
      <div className="flex flex-col gap-4">
        {actionFailed ? (
          <p role="alert" className="flex items-center gap-2 text-sm text-danger">
            <StatusDot tone="alert" />
            {SPACE_LIST_COPY.members.failed}
          </p>
        ) : null}

        {notice === undefined ? null : (
          /*
            `AddAgencyPanel`'s answer, shown on the roster it handed over to.
            A `role="status"` rather than an `alert`: nothing failed, and this is
            the designed outcome of the act that just ran (ADR-0013's rule).

            Above the roster, not beside the new row — the row it is about looks
            like every other `Invited` one, which is the whole reason the
            sentence has to travel here at all.
          */
          <p role="status" className="flex items-start gap-2 text-sm">
            <StatusDot tone="blue" />
            {notice}
          </p>
        )}

        {viewerIsOwner ? (
          // CON-22: the owner alone adds a member (§5.3). Visibility, not
          // authorization — the API refuses a non-owner's POST regardless.
          <div>
            <button
              type="button"
              onClick={onAddAgency}
              className={BUTTON_CLASS}
              style={primaryButtonStyle()}
            >
              {SPACE_LIST_COPY.members.add}
            </button>
          </div>
        ) : null}

        {space.members.length <= 1 ? (
          <p className="text-sm" style={{ color: token('bp-text-secondary') }}>
            {SPACE_LIST_COPY.members.empty}
          </p>
        ) : null}

        {/*
          A plain list, in the order the server sent — owner first, then by name
          (`resolveRoster`), so a roster renders the same way twice. No sorting
          here: a client-side sort would order one reading of the room and call
          it the room.
        */}
        <ul className="flex flex-col divide-y" style={{ borderColor: token('bp-border') }}>
          {space.members.map((member) => (
            <li key={member.agencyId} className="py-3 first:pt-0">
              <MemberRow
                member={member}
                isViewer={member.agencyId === viewerAgencyId}
                viewerIsOwner={viewerIsOwner}
                busy={busyAgencyId === member.agencyId}
                confirming={confirming?.agencyId === member.agencyId ? confirming : undefined}
                onAsk={(asked) => {
                  setActionFailed(false);
                  setConfirming({ agencyId: member.agencyId, ...asked });
                }}
                onCancel={() => {
                  setConfirming(undefined);
                }}
                onConfirm={(asked) => {
                  void run(member.agencyId, () =>
                    asked.kind === 'scope'
                      ? setMemberScope(space.id, member.agencyId, asked.scope)
                      : removeMembership(space.id, member.agencyId),
                  );
                }}
                onAccept={() => {
                  void run(member.agencyId, () => acceptMembership(space.id));
                }}
              />
            </li>
          ))}
        </ul>

        {/*
          CON-26, and it sits below the roster rather than beside Add an agency
          at the top: both are the owner's acts on the room, but one of them
          ends it, and a destructive control directly above a list of the people
          it affects is a misread waiting to happen.

          `viewerIsOwner` is visibility, not authorization — the API answers 403
          for a member that does not own the room, and 409 while any file
          survives (SEC-5).
        */}
        {viewerIsOwner ? (
          <div
            className="flex flex-col items-start gap-2 border-t pt-4"
            style={{ borderColor: token('bp-border') }}
          >
            {deleteAsked ? (
              <>
                <p className="text-sm font-medium" style={{ color: token('bp-text') }}>
                  {SPACE_LIST_COPY.members.deleteSpace.confirm.title}
                </p>
                <p className="text-xs" style={{ color: token('bp-text-secondary') }}>
                  {SPACE_LIST_COPY.members.deleteSpace.confirm.body}
                </p>
                {/*
                  The reassurance, and it is not padding: an admin's real
                  question here is whether they are about to destroy evidence,
                  and the answer is no.
                */}
                <p className="text-xs" style={{ color: token('bp-text-muted') }}>
                  {SPACE_LIST_COPY.members.deleteSpace.confirm.keeps}
                </p>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setDeleteAsked(false);
                    }}
                    className={BUTTON_CLASS}
                    style={neutralButtonStyle()}
                  >
                    {SPACE_LIST_COPY.members.deleteSpace.confirm.cancel}
                  </button>

                  <button
                    type="button"
                    disabled={deleting}
                    onClick={() => void removeSpace()}
                    className={BUTTON_CLASS}
                    style={dangerButtonStyle()}
                  >
                    {deleting
                      ? SPACE_LIST_COPY.members.deleteSpace.working
                      : SPACE_LIST_COPY.members.deleteSpace.confirm.submit}
                  </button>
                </div>
              </>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setDeleteError(undefined);
                  setDeleteAsked(true);
                }}
                className={BUTTON_CLASS}
                style={neutralButtonStyle()}
              >
                {SPACE_LIST_COPY.members.deleteSpace.label}
              </button>
            )}

            {deleteError === undefined ? null : (
              <p role="alert" className="flex items-start gap-2 text-sm text-danger">
                <StatusDot tone="alert" />
                {deleteError}
              </p>
            )}
          </div>
        ) : null}
      </div>
    </DetailDrawer>
  );

  async function removeSpace() {
    setDeleting(true);
    setDeleteError(undefined);

    try {
      await deleteSpace(space.id);
      // The room is gone, so there is no roster left to hand back to — the
      // caller closes the drawer and refetches.
      onDeleted();
    } catch (caught) {
      // The 409 is the case worth naming, and the API's own message already
      // carries both steps of the remedy. `messageFrom` prefers it; the
      // fallback is the same two steps for when there is no body to read.
      setDeleteError(
        messageFrom(
          caught,
          caught instanceof ApiError && caught.status === 409
            ? SPACE_LIST_COPY.members.deleteSpace.conflict
            : SPACE_LIST_COPY.members.deleteSpace.failed,
        ),
      );
    } finally {
      setDeleting(false);
    }
  }
}

/**
 * One agency on the roster.
 *
 * The identity half — glyph, name, domain, owner mark, status — is shown to
 * every member (CON-11 as amended). The acts below it are gated on what the API
 * would allow, and the gate is different for the reader's own row: **leaving is
 * the reader's own act and removing is the owner's act on somebody else**, one
 * route apart only by its target (ADR-0071). They read differently because they
 * are different — an owner cannot refuse a member's departure, and a member
 * cannot evict a third party.
 */
function MemberRow({
  member,
  isViewer,
  viewerIsOwner,
  busy,
  confirming,
  onAsk,
  onCancel,
  onConfirm,
  onAccept,
}: {
  member: SpaceMemberRef;
  isViewer: boolean;
  viewerIsOwner: boolean;
  busy: boolean;
  confirming: Confirming | undefined;
  onAsk: (asked: Asked) => void;
  onCancel: () => void;
  onConfirm: (asked: Asked) => void;
  onAccept: () => void;
}) {
  /*
    **A confirmation gets its own row; every other act sits beside the name.**

    The resting layout is `justify-between` over an identity block and a
    `shrink-0` control, which is right for an overflow trigger or a two-word
    button. A confirmation is neither: its label names the agency *and* what the
    act does to it — "Confirm: BP Clerk sees every file here" — and two of those
    refusing to shrink inside a `max-w-md` drawer overflowed the row, collapsing
    the `min-w-0` block beside them until the agency's name broke across two
    lines and the amber term-end date wrapped one word per line.

    Shrinking the buttons is the wrong fix: the whole point of the pair is that
    somebody reads it before pressing it. It goes below instead, where the
    sentence has the drawer's full width.
  */
  const confirmingHere = confirming !== undefined;

  return (
    <div
      className={confirmingHere ? 'flex flex-col gap-3' : 'flex items-start justify-between gap-3'}
    >
      <div className="flex min-w-0 items-start gap-2">
        {/*
          CON-21's glyph, or nothing — `agencyType` is optional on the wire
          (`src/lib/api.ts`) because it predates most fixtures in this
          workspace, and an absent type must render as no icon rather than a
          broken one. `mt-0.5 shrink-0` is `FolderView`'s two-line-cell pattern:
          this block stacks a name over a domain.
        */}
        {member.agencyType === undefined ? null : (
          <span className="mt-0.5 shrink-0">
            <AgencyTypeIcon
              type={member.agencyType}
              label={SPACE_LIST_COPY.members.agencyTypes[member.agencyType]}
            />
          </span>
        )}

        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium" style={{ color: token('bp-text') }}>
              {member.name}
            </span>
            {member.isOwner ? <Badge tone="info">{SPACE_LIST_COPY.members.owner}</Badge> : null}
            {/*
              **Status only where it is not `Active`** (FIL-2 as amended). A
              badge on every row reading "Active" is furniture; the one that
              says an agency has not answered is the fact an owner opened this
              panel for.
            */}
            {member.status === 'Active' ? null : (
              <Badge tone={STATUS_TONE[member.status]}>
                {SPACE_LIST_COPY.members.status[member.status]}
              </Badge>
            )}
            {/*
              CON-24 as amended: beside the status, on every row, for every
              reader. **Unconditional, unlike the status badge above** — and the
              asymmetry is the point rather than an oversight. A status badge
              reading "Active" is furniture because `Active` is what a membership
              normally is; neither scope is the normal one, so an absent badge
              would be a reader guessing which of two answers "no badge" meant.

              `neutral` for both values, deliberately. A tone that made `own`
              look like a warning or `all` look like a grant would editorialise a
              compartmentation decision an owner made on purpose.
            */}
            <Badge tone="neutral">{SPACE_LIST_COPY.members.scopes[member.visibilityScope]}</Badge>
          </div>

          {/*
            `bp-text-secondary`, not `bp-text-muted`. The muted rung is 4.1:1 on
            this background — under AA at this size, and a domain is exactly the
            kind of string a reader has to be able to check.
          */}
          <span className="text-xs" style={{ color: token('bp-text-secondary') }}>
            {member.domain}
          </span>

          {/*
            EXIT-7 / A-8, and this panel is its only surface in the product: it
            left the Spaces table and then the Files card on product feedback
            that called it noise on both, and one date per member could not have
            lived on either anyway. Absent whenever the agency holds no plan.
          */}
          {member.termEndsAt === undefined ? null : (
            <span className="text-xs" style={{ color: token('bp-amber') }}>
              {SPACE_LIST_COPY.members.termEnds(
                formatUtcDate(member.termEndsAt, SPACE_LIST_COPY.dateLocale),
              )}
            </span>
          )}
        </div>
      </div>

      <MemberActions
        member={member}
        isViewer={isViewer}
        viewerIsOwner={viewerIsOwner}
        busy={busy}
        confirming={confirming}
        onAsk={onAsk}
        onCancel={onCancel}
        onConfirm={onConfirm}
        onAccept={onAccept}
      />
    </div>
  );
}

function MemberActions({
  member,
  isViewer,
  viewerIsOwner,
  busy,
  confirming,
  onAsk,
  onCancel,
  onConfirm,
  onAccept,
}: {
  member: SpaceMemberRef;
  isViewer: boolean;
  viewerIsOwner: boolean;
  busy: boolean;
  confirming: Confirming | undefined;
  onAsk: (asked: Asked) => void;
  onCancel: () => void;
  onConfirm: (asked: Asked) => void;
  onAccept: () => void;
}) {
  // CON-6, on this agency's own membership and no other. Accepting is one
  // agency's act, so the row that offers it is the row that names us.
  if (isViewer && member.status === 'Invited') {
    return (
      <button
        type="button"
        disabled={busy}
        onClick={onAccept}
        className={`${BUTTON_CLASS} shrink-0`}
        style={primaryButtonStyle()}
      >
        {busy ? SPACE_LIST_COPY.members.accepting : SPACE_LIST_COPY.members.accept}
      </button>
    );
  }

  /*
    **Two acts, two gates, and they do not coincide.**

    Ending a membership needs an `Active` one to end (CON-14), and it is the
    reader's own row (leave) or the owner's on somebody else's (remove) — nobody
    may end a third party's.

    Setting a scope is the owner's alone (§5.3) and runs on an `Invited`
    membership too: the two facts a member is added with are its clearance and
    its scope (CON-23), and arranging a room before anybody walks into it is the
    joint docket CON-24 exists for. It is withheld on the owner's own row — see
    this module's header for the consequence that gate has behind it.
  */
  const ending = isViewer ? 'leave' : viewerIsOwner ? 'remove' : undefined;
  const endable = member.status === 'Active' ? ending : undefined;
  const scopeTo =
    viewerIsOwner && !isViewer && member.status !== 'Removed'
      ? otherScope(member.visibilityScope)
      : undefined;

  // A membership that has already ended offers nothing: re-entry is a fresh
  // invitation and an acceptance (CON-14), which is the owner's act on the Add
  // panel rather than an undo on this row.
  if (endable === undefined && scopeTo === undefined) return null;

  /*
    **The confirmation is outside the menu here.** `SpaceList`'s own
    `RowActionsMenu` and the Users surface's `MemberRowActions` moved theirs
    inside their panels; this roster overflow did not, and a click on the item
    still closes the menu and renders this pair in its row instead, so nobody
    has to reopen a menu to see the two words that decide it. Two-step for
    CON-14's verbs because neither is undoable in any sense an admin would
    recognise, and two-step for a scope change because it changes what another
    organisation can read — a widening as much as a narrowing.
  */
  if (confirming !== undefined) {
    const scope = confirming.kind === 'scope' ? confirming.scope : undefined;

    return (
      // `flex-wrap`, and no `shrink-0`: `MemberRow` gives this pair a row of its
      // own, and on a narrow drawer the two sentences stack rather than overrun
      // it. The resting controls below keep `shrink-0` — they sit beside a name.
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            onConfirm(confirming);
          }}
          /*
            **The one button in this directory that wraps.** `BUTTON_CLASS`
            carries `whitespace-nowrap`, which is right for every label it was
            written for and wrong here: this one is a sentence naming an agency,
            and "Confirm: St. Martin Parish Sheriff's Department sees every file
            here" is wider than the drawer at any weight.

            A sibling class rather than an override — `spaceControls.ts` has why
            appending `whitespace-normal` to `BUTTON_CLASS` does nothing at all.
          */
          className={WRAPPING_BUTTON_CLASS}
          /*
            Neutral for a scope change, danger for CON-14's two. The danger
            treatment is reserved for an act that takes a room away; re-scoping
            takes nothing away that a second click cannot put back (CON-25),
            and spending the colour on it would leave nothing to distinguish
            the act that cannot be undone.
          */
          style={scope === undefined ? dangerButtonStyle() : neutralButtonStyle()}
        >
          {busy
            ? scope !== undefined
              ? SPACE_LIST_COPY.members.savingScope
              : confirming.kind === 'leave'
                ? SPACE_LIST_COPY.members.leaving
                : SPACE_LIST_COPY.members.removing
            : scope !== undefined
              ? SPACE_LIST_COPY.members.confirmScope[scope](member.name)
              : confirming.kind === 'leave'
                ? SPACE_LIST_COPY.members.confirmLeave
                : SPACE_LIST_COPY.members.confirmRemove(member.name)}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className={BUTTON_CLASS}
          style={neutralButtonStyle()}
        >
          {scope !== undefined
            ? SPACE_LIST_COPY.members.cancelScope
            : confirming.kind === 'leave'
              ? SPACE_LIST_COPY.members.cancelLeave
              : SPACE_LIST_COPY.members.cancelRemove}
        </button>
      </div>
    );
  }

  return (
    <span className="shrink-0">
      <RowActionsMenu
        label={SPACE_LIST_COPY.members.moreFor(member.name)}
        glyph={SPACE_LIST_COPY.members.moreGlyph}
      >
        {/*
          Scope first, and ending second, in the order a menu should offer them:
          the reversible act above the one that is not.
        */}
        {scopeTo === undefined ? null : (
          <RowActionsItem
            label={SPACE_LIST_COPY.members.setScope[scopeTo](member.name)}
            onSelect={() => {
              onAsk({ kind: 'scope', scope: scopeTo });
            }}
          />
        )}
        {endable === undefined ? null : (
          <RowActionsItem
            label={
              endable === 'leave' ? SPACE_LIST_COPY.members.leave : SPACE_LIST_COPY.members.remove
            }
            tone="danger"
            onSelect={() => {
              onAsk({ kind: endable });
            }}
          />
        )}
      </RowActionsMenu>
    </span>
  );
}
